import { useTranslation } from "react-i18next";
import { ApiError } from "../api/client.js";

const KNOWN = new Set([
  "ValidationFailed",
  // apps/api/src/modules/users/routes.ts uses "Validation" instead of
  // "ValidationFailed" on 7 endpoints. That is an inconsistency in the API,
  // not a second concept: it resolves to the identical catalogue text in
  // both locales (see api-error.test.ts), not a separate one. Don't "tidy"
  // this away as a duplicate without also fixing the API.
  "Validation",
  "Unauthorized", "Forbidden", "NotFound", "Conflict", "RateLimited", "Internal",
]);

/**
 * The catalogue key for a thrown error.
 *
 * The API's own `message` is never used. It is a mixture of German and English
 * prose written for developers, it reaches the audit log and the server's logs,
 * and showing it to a user means showing whichever language the developer of
 * that endpoint happened to type.
 */
export function apiErrorKey(e: unknown): string {
  if (e instanceof ApiError && KNOWN.has(e.code)) return `errors.${e.code}`;
  return "errors.generic";
}

/**
 * A minimal stand-in for react-i18next's TFunction — just enough surface for
 * resolveApiErrorMessage below. Mirrors the same seam enum-label.ts uses for
 * resolveEnumLabel, so this is testable with a plain stub instead of needing
 * to render a component to reach useTranslation().
 */
type Translate = (key: string) => string;

/**
 * Pure lookup logic behind useApiErrorMessage(), factored out so it is
 * testable directly with a stub translator (see enum-label.ts for the same
 * pattern).
 *
 * `overrides` lets a call site supply the context it already holds that the
 * bare error code can't express. A `Conflict` from "toggle a user's active
 * flag" means something different from a `Conflict` from "create an asset",
 * and the code alone can't tell them apart. Keys are matched in this order:
 *   1. a literal ApiError `code` (e.g. "Conflict") — checked first and wins
 *      even when that code is already in KNOWN, since a caller-specific
 *      override is by definition more specific than the generic catalogue
 *      text for that code.
 *   2. the reserved key `"fallback"` — used only when the code doesn't
 *      resolve to anything more specific (unrecognised code, or the thrown
 *      value isn't an ApiError at all). This is the direct replacement for
 *      the old single fallbackKey parameter.
 * With no overrides at all, behaviour is identical to apiErrorKey.
 */
export function resolveApiErrorMessage(t: Translate, e: unknown, overrides?: Record<string, string>): string {
  const code = e instanceof ApiError ? e.code : undefined;
  if (code && overrides?.[code] !== undefined) return t(overrides[code]!);
  const key = apiErrorKey(e);
  if (key === "errors.generic" && overrides?.fallback) return t(overrides.fallback);
  return t(key);
}

/**
 * The translated message for a thrown error, optionally with per-code
 * overrides (and/or a "fallback" override for anything unrecognised) — see
 * resolveApiErrorMessage.
 */
export function useApiErrorMessage() {
  const { t } = useTranslation();
  return (e: unknown, overrides?: Record<string, string>): string =>
    resolveApiErrorMessage((key) => t(key as never), e, overrides);
}
